import { Avatar, Box, Text, useComputedColorScheme } from '@mantine/core';
import React, { ReactNode } from 'react';
import { useUserPhotoByName } from '@repo/utils';
import { errorBoundaryWrapper } from '@repo/utils';

/**
 * A variant of DetailsItem where the left accent strip is replaced by a
 * circular person avatar (photo resolved from Graph API, falling back to
 * teal initials).  The title and text slots work the same as DetailsItem.
 */
const PersonDetailItem = ({
  name,
  title,
  text,
  isBlurred = false,
}: {
  /** Display name used for Graph photo lookup AND for deriving initials */
  name: string;
  title: string;
  text: ReactNode;
  isBlurred?: boolean;
}) => {
  const { photoUrl, isLoading } = useUserPhotoByName(name);
  const scheme = useComputedColorScheme('light');
  const isDark = scheme === 'dark';

  const bg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';

  // Derive initials: works for "First Last" and "Last, First"
  const initials = (() => {
    const parts = name.split(/[\s,]+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?';
    return ((parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '')).toUpperCase();
  })();

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'row',
        borderRadius: 6,
        overflow: 'hidden',
        background: bg,
        minHeight: 52,
        alignItems: 'center',
      }}
    >
      {/* Avatar replaces the accent bar on the left */}
      <Box
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 44,
          alignSelf: 'stretch',
          background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
        }}
      >
        <Avatar
          src={photoUrl ?? null}
          size={32}
          radius='xl'
          color='teal'
          style={{
            opacity: isLoading ? 0.5 : 1,
            transition: 'opacity 0.25s',
          }}
        >
          {!photoUrl && initials}
        </Avatar>
      </Box>

      {/* Content — mirrors DetailsItem layout exactly */}
      <Box px={10} py={6} style={{ flex: 1, minWidth: 0 }}>
        {/* Label */}
        <Text
          fz={10}
          fw={600}
          tt='uppercase'
          c='dimmed'
          style={{ letterSpacing: '0.06em', lineHeight: 1, marginBottom: 2 }}
        >
          {title}
        </Text>

        {/* Value */}
        <Text
          fz='sm'
          fw={600}
          style={{
            lineHeight: 1.3,
            wordBreak: 'break-word',
            ...(isBlurred ? { filter: 'blur(5px)', userSelect: 'none' } : {}),
          }}
        >
          {text}
        </Text>
      </Box>
    </Box>
  );
};

export default errorBoundaryWrapper(PersonDetailItem);

